// Catalogue i18n namespace (Module 01 §4). This is the shared key scaffold every
// Stage-2 screen builder extends. It is wired into apps/web's aggregated
// `dictionaries` (src/i18n-dictionaries.ts) so the CI key-parity gate
// (`pnpm run i18n:parity`, TESTING §12.1 / D-014) covers it automatically.
//
// `catalogueEn` is authoritative; `CatalogueDict` widens its values to `string`
// (src/i18n-shape.ts) so every locale stays parity-checked at COMPILE time (a
// missing/extra key fails tsc) while carrying real translations.
//
// ru IS TRANSLATED — catalogue.ru.ts. fr, zh, zh-Hant and hi are machine-translated
// in catalogue.<locale>.ts. Declared in src/i18n-completeness.ts, enforced by
// tools/ci/i18n-parity.ts.
//
// A key you add here no longer flows to all four locales for free: tsc will
// demand it in catalogue.ru.ts, and the gate will demand a real Russian value.
//
// Stage-2 builders: add your screen's keys under the matching section below.
// Keep this file the single catalogue namespace; nest per screen so parallel
// edits touch different regions.

import type { Translated } from "../../../i18n-shape.js";
import { catalogueRuDict } from "./catalogue.ru.js";

export const catalogueEn = {
  // Office section navigation (CatalogueLayout — the §3.10 RouterTabs strip).
  nav: {
    label: "Catalogue sections",
    products: "Products",
    uoms: "Units",
    costRuns: "Cost runs",
    templates: "Templates",
    imports: "Import",
  },

  // Screen titles (S1–S9). Stage-2 builders own the body of each screen; these
  // titles seed the route stubs and stay stable.
  screens: {
    products: "Products",
    productDetail: "Product",
    bomEditor: "Bill of materials",
    routingEditor: "Routing",
    uoms: "Units of measure",
    imports: "Import from Excel",
    costRuns: "Cost runs",
    costRunDetail: "Cost run",
    templates: "Templates",
    floorLookup: "Product lookup",
  },

  // S6 Imports (screens/imports.tsx): the page a reader without a catalogue
  // import lane reads.
  imports: {
    forbiddenTitle: "Not available to your role",
    forbiddenBody:
      "Importing products, materials, bills of materials and routings is for planners and the admins.",
  },

  // Shared request-state copy (§4 mandates loading / error / empty on every
  // screen). Screens pass these into the shared state primitives.
  state: {
    loading: "Loading…",
    errorTitle: "Something went wrong",
    errorDetail: "The request could not be completed. Try again.",
    retry: "Retry",
    requestId: "Request ID",
    empty: "Nothing here yet.",
    // §3.8: every confirmation banner can be dismissed; every modal can be closed.
    dismiss: "Dismiss",
    close: "Close",
  },

  // Traceability modes (contracts `ProductTracking`). A user-facing enum needs a
  // user-facing name: "measured_unit" is a column value, "Measured unit" is what
  // a storekeeper reads.
  tracking: {
    none: "None",
    lot: "Lot",
    serial: "Serial",
    measured_unit: "Measured unit",
  },

  // StatusBadge labels — every lifecycle status across products, BOMs, routings,
  // cost runs, UoMs, schemas and templates (§2, §3).
  status: {
    draft: "Draft",
    active: "Active",
    discontinued: "Discontinued",
    superseded: "Superseded",
    archived: "Archived",
    computing: "Computing",
    computed: "Computed",
    compute_failed: "Compute failed",
    released: "Released",
    discarded: "Discarded",
    // NOT a wire status. An `active` version whose `effective_from` is still
    // ahead of today reads "Scheduled" on the version screens (spec 01 §9.16,
    // D-261) — the badge said "Active" while §3.4 resolved today to an older
    // version, which is how a 2030 activation went unnoticed for weeks.
    scheduled: "Scheduled",
  },

  // Schema-driven attribute form (§2.3, §3.1). Client-side validation of the
  // documented JSON-Schema subset; server field errors are surfaced verbatim.
  attr: {
    optional: "optional",
    selectPlaceholder: "Select…",
    booleanTrue: "Yes",
    booleanFalse: "No",
    unsupportedField: "This field type is not editable here.",
    errors: {
      required: "This field is required.",
      type: "The value has the wrong type.",
      enum: "Choose one of the allowed values.",
      pattern: "The value does not match the required format.",
      unknownKey: "This attribute is not allowed by the schema.",
      number: "Enter a number.",
      integer: "Enter a whole number.",
    },
  },

  // Redaction-aware cost display (§2.16, §7). Rendered only when the cost field
  // is present in the API response; a role without catalogue.cost.read never
  // sees these because the server omits the fields entirely.
  cost: {
    none: "—",
    perUnit: "per {{qty}}",
    title: "Cost",
    material: "Material",
    labor: "Labour",
    overhead: "Overhead",
    outsourcing: "Outsourcing",
    byProductCredit: "By-product credit",
    total: "Total",
    unitCost: "Unit cost",
    breakdown: "Breakdown",
    nrv: "Net realisable value",
    lines: "Components",
    steps: "Operations",
    credits: "Credits",
    apportionment: "Apportionment",
  },

  // S7 Cost runs (/catalogue/cost-runs + /:id). Every screen here lives behind
  // catalogue.cost.read (server-enforced); Release additionally needs
  // catalogue.cost.release. These keys own the S7 list + detail surface.
  costRun: {
    // List (S7 list).
    listTitle: "Cost runs",
    newRun: "New cost run",
    loadingList: "Loading cost runs…",
    listErrorTitle: "Could not load cost runs",
    listEmptyTitle: "No cost runs yet",
    listEmptyBody: "Run a standard cost roll-up to value your make products.",
    colAsOf: "As of",
    colScope: "Scope",
    colFacility: "Facility",
    colStatus: "Status",
    colCurrency: "Currency",
    colCreated: "Created",
    groupWide: "Group-wide",
    scope: {
      all_make_products: "All make products",
      selected_products: "Selected products",
    },
    // Access — the whole route is cost-gated; a role without cost.read gets an
    // explicit "not available" state rather than a broken/retryable screen.
    forbiddenTitle: "Cost data is not available",
    forbiddenBody: "You do not have permission to view cost runs.",
    // Create form.
    createTitle: "New cost run",
    fieldAsOf: "As-of date",
    fieldScope: "Scope",
    fieldFacility: "Facility ID (optional)",
    facilityHint: "Leave blank for a group-wide run.",
    fieldNote: "Note (optional)",
    fieldProductIds: "Product IDs",
    productIdsHint: "One product ID per line.",
    create: "Start roll-up",
    creating: "Starting…",
    createError: "Could not start the cost run. Check the fields and try again.",
    errorProductIdsRequired: "Add at least one product ID for a selected-products run.",
    errorAsOfRequired: "Choose an as-of date.",
    cancel: "Cancel",
    // Detail (S7 detail).
    detailLoading: "Loading cost run…",
    detailErrorTitle: "Could not load the cost run",
    paramsTitle: "Parameters",
    asOfLabel: "As of",
    scopeLabel: "Scope",
    facilityLabel: "Facility",
    currencyLabel: "Currency",
    noteLabel: "Note",
    createdLabel: "Created",
    releasedLabel: "Released",
    computingHint: "This run is still computing — per-product results appear when it finishes.",
    failedHint: "This run failed to compute. Start a new run.",
    // Why it failed, one sentence per `CostRunFailureCode`. The SERVER sends
    // the code and the pointers and never the sentence (D-225): before that
    // ruling this text was English prose minted in `cost.service.ts` and stored
    // in the creator's own `note` column, so it could never be translated and
    // it erased what they had typed. `{{sku}}` is the product the roll-up
    // stopped on. A run that predates the columns falls back to `failedHint`.
    failure: {
      APPORTIONMENT_SUM_INVALID:
        "Stopped on {{sku}}: its co-product apportionment adds up to 100% or more, so the main product would be left with no cost.",
      BOM_CYCLE_DETECTED:
        "Stopped on {{sku}}: its BOM contains itself, directly or through a component.",
      BOM_DEPTH_EXCEEDED:
        "Stopped on {{sku}}: its BOM nests more than 12 levels deep, past the limit a roll-up can explode.",
    },
    failureOpenBom: "Open the BOM",
    discardedHint: "This run was discarded and can no longer be released.",
    // Per-product table.
    itemsTitle: "Per-product costs",
    colProduct: "Product",
    colOldCost: "Old cost",
    colNewCost: "New cost",
    colDelta: "Δ%",
    colLevel: "Level",
    itemsLoading: "Loading cost items…",
    itemsErrorTitle: "Could not load cost items",
    itemsEmptyTitle: "No per-product results",
    itemsEmptyBody: "This cost run produced no cost items.",
    itemsFilteredEmpty: "No products meet the Δ% filter.",
    itemsFilteredEmptyBody: "Lower the Δ% threshold to see the rest of the run.",
    deltaNew: "New",
    minDeltaLabel: "Min Δ%",
    minDeltaAll: "All",
    minDeltaOption: "≥ {{pct}}%",
    loadingMore: "Loading more…",
    showBreakdown: "Show breakdown",
    hideBreakdown: "Hide breakdown",
    // Release.
    release: "Release",
    releasing: "Releasing…",
    releasedNotice: "Released — {{count}} products now carry this run's standard cost.",
    discardedNotice: "This run was discarded. Nothing was re-valued.",
    releaseConfirmTitle: "Release this cost run?",
    releaseConfirmBody: "{{count}} products will be re-valued to the new standard cost.",
    releaseConfirm: "Release",
    releaseError: "Could not release the cost run.",
    // Discard.
    discard: "Discard",
    discarding: "Discarding…",
    discardConfirmTitle: "Discard this cost run?",
    discardConfirmBody: "This cost run will be discarded and can no longer be released.",
    discardConfirm: "Discard",
    discardError: "Could not discard the cost run.",
  },

  // Effectivity timeline bar (S2/S3/S4 version effectivity).
  effectivity: {
    title: "Effectivity",
    current: "Current",
    open: "Open-ended",
    none: "No effective version.",
    from: "From",
    to: "To",
  },

  // "Which version is effective TODAY" — the §3.4 resolver's answer, printed
  // on S3/S4 (components/effective-today.tsx; spec 01 §9.16, D-261). `none` is
  // the resolver's 404: a fact to act on, never an error.
  effectiveToday: {
    loading: "Working out which version is effective today…",
    resolved: "Effective today ({{date}}): version {{version}}.",
    thisVersion: "This version is effective today ({{date}}).",
    none: "No version is effective today ({{date}}).",
    noneHint: "Planning and costing cannot resolve one until a version is activated.",
    error: "Could not work out which version is effective today.",
    open: "Open version {{version}}",
  },

  // Withdraw a SCHEDULED activation (components/withdraw-dialog.tsx; spec 01
  // §3.3.5, D-261). Shared by the BOM and routing editors: every sentence is
  // phrased around "version", which keeps the Russian gender-neutral across a
  // feminine спецификация and a masculine маршрут.
  withdraw: {
    button: "Withdraw",
    title: "Withdraw this scheduled activation",
    lead: "Version {{version}} starts on {{date}}. Withdrawing archives it; today's version stays.",
    restorePreview: "Version {{version}} (superseded) will return to active.",
    noRestorePreview:
      "No earlier version will be restored — nothing will be active until a version is activated.",
    // The predecessor has been ARCHIVED (housekeeping ran while this version
    // was scheduled): a different fact from "nothing to restore", and the
    // server refuses it by name. Said before the reason is typed.
    notRestorablePreview:
      "Version {{version}}, which this one replaced, has since been archived, so this withdrawal cannot go through. Activate a new version instead.",
    unknownRestorePreview:
      "This screen cannot tell which version comes back. Withdrawing restores the version this one replaced, and the result names it.",
    reason: "Reason",
    reasonPlaceholder: "Why is this activation being withdrawn? (required)",
    reasonRequired: "A reason is required.",
    cancel: "Cancel",
    confirm: "Withdraw",
    withdrawing: "Withdrawing…",
    close: "Close",
    // The three §3.3.5 refusals, each its own sentence — a refusal is a stable
    // fact about the row, so none of these invites a retry.
    refusal: {
      inForce:
        "This version took effect on {{date}} and can no longer be withdrawn — a version in force is superseded by a newer one, never undone.",
      serverToday: "Today's date in the system is {{today}}.",
      notActive:
        "This version is no longer active — it may already have been withdrawn or superseded. The screen has been refreshed.",
      predecessorNotRestorable:
        "The version this one superseded can no longer be restored, so the withdrawal was refused. Activate a new version instead.",
    },
    failed: "Could not withdraw this version.",
    resultRestored: "Version {{withdrawn}} withdrawn. Version {{restored}} is active again.",
    // The restored id is not in the version list on screen (a stale list, or a
    // scope this list does not show): name the id rather than print a blank.
    resultRestoredById:
      "Version {{withdrawn}} withdrawn. The version it superseded is active again (id {{id}}).",
    resultNone:
      "Version {{withdrawn}} withdrawn. No version is active now — activate one before planning or costing needs it.",
    openRestored: "Open version {{version}}",
  },

  // Archive a version (components/archive-dialog.tsx; spec 01 §3.3.5 Archive,
  // D-261 amended 2026-09-08). Shared by the BOM and routing editors, phrased
  // around "version" for the same gender-neutral reason as `withdraw`.
  archive: {
    button: "Archive",
    title: "Archive this version",
    leadDraft:
      "Version {{version}} is a draft. Archiving abandons it: it can no longer be edited or activated. This cannot be undone.",
    leadSuperseded:
      "Version {{version}} is superseded. Archived, it stays in the history but can't be brought back. This can't be undone.",
    // The row's own range has not ended by this screen's calendar — the
    // version that superseded it is not yet in force, and a withdrawal would
    // restore this one. The server refuses by name; said before the press.
    resumablePreview:
      "This version runs to {{date}}, and its successor isn't in force yet. Withdraw the scheduled successor before archiving this one.",
    cancel: "Cancel",
    confirm: "Archive",
    archiving: "Archiving…",
    close: "Close",
    // The two §3.3.5 archive refusals, each its own sentence — a refusal is a
    // stable fact about the row, so neither invites a retry.
    refusal: {
      stillResumable:
        "This version runs to {{date}} and would return if its scheduled successor were withdrawn. Withdraw that version first.",
      stillResumableNoDate:
        "This version would return if its scheduled successor were withdrawn. Withdraw that version first.",
      notArchivable:
        "This version is now “{{status}}”, and only a draft or superseded version can be archived. The screen has been refreshed.",
      notArchivableNoStatus:
        "This version's status changed before it could be archived. Only a draft or superseded version can be archived. The screen has been refreshed.",
    },
    failed: "Could not archive this version.",
    result: "Version {{version}} archived.",
  },

  // Version switcher + side-by-side diff (S3/S4).
  version: {
    label: "Version",
    switch: "Switch version",
    compare: "Compare",
    against: "Compare against",
  },
  diff: {
    added: "Added",
    removed: "Removed",
    changed: "Changed",
    unchanged: "Unchanged",
    noChanges: "No differences.",
    before: "Before",
    after: "After",
  },

  // Floor lookup (S9, floor-pwa area). Screen group: floor-lookup. All keys the
  // S9 screen renders live here so the i18n:parity gate (D-014) covers them in
  // every locale; a key added here must be added to every locale's file too.
  floorLookup: {
    // Scan entry.
    scanPrompt: "Scan a product QR code.",
    scanLabel: "Scan or enter a product code",
    scanPlaceholder: "Scan QR or paste product link…",
    scanHint: "Point the scanner at the QR on the product card, or paste its link.",
    lookup: "Look up",
    clear: "Clear",
    scanUnreadable: "That code could not be read. Scan the product-card QR again.",
    // Cache / connectivity banner.
    offline: "Offline — showing cached catalogue.",
    online: "Online",
    syncing: "Syncing catalogue…",
    refresh: "Refresh",
    cacheAge: "Cache updated {{age}}",
    cacheNever: "Not synced yet",
    ageJustNow: "just now",
    ageMinutes: "{{n}} min ago",
    ageHours: "{{n}} h ago",
    ageDays: "{{n}} d ago",
    // Facility provisioning (this device's facility).
    facility: "Facility",
    changeFacility: "Change facility",
    facilityPromptTitle: "Set this device's facility",
    facilityPromptBody:
      "Choose the facility this station belongs to so its catalogue can sync for offline use.",
    facilityPick: "Use this facility",
    facilityCancel: "Keep the current facility",
    facilityErrorTitle: "Could not load facilities",
    facilityErrorBody:
      "The facility list could not be loaded. Connect to the network and try again.",
    facilityEmptyTitle: "No facilities yet",
    facilityEmptyBody: "Ask the office to add this plant's facility before setting up this device.",
    // Sync failure with no usable cache (error card, §4).
    syncErrorTitle: "Could not sync the catalogue",
    syncErrorBody: "The catalogue could not be loaded and no offline copy is stored yet.",
    // Cache empty (offline, nothing synced yet).
    cacheEmptyTitle: "Catalogue not synced",
    cacheEmptyBody: "Connect to sync this facility's catalogue for offline use.",
    // Product resolution outcomes.
    notFoundTitle: "Product not found",
    notFoundBody: "No active product at this facility matches this code.",
    notFoundOfflineBody: "This product is not in the offline cache. Reconnect to look it up.",
    // Read-only product card (§4 S9: no cost, no BOM quantities).
    card: {
      sku: "SKU",
      name: "Name",
      attributes: "Attributes",
      noAttributes: "No attributes recorded.",
      routing: "Routing — this facility",
      noRouting: "No active routing is cached for this facility.",
      step: "Step {{no}}",
      instructions: "Instructions",
      noInstructions: "No instructions.",
      outsourced: "Outsourced",
      measured: "Measured capture",
      serial: "Serial capture",
      qcGate: "Quality gate",
    },
  },

  // ---- S1 Product list (/catalogue/products) -------------------------------
  // Owned by the Stage-2 "products" screen group. Nested here so parity is
  // gated across every locale.
  productsList: {
    title: "Products",
    new: "New product",
    searchLabel: "Search",
    searchPlaceholder: "SKU or name…",
    loading: "Loading products…",
    errorTitle: "Could not load products",
    // §4: empty = one-line explanation + primary CTA.
    emptyTitle: "No products yet",
    emptyBody: "Start from a template or import your price list.",
    emptyCta: "New product",
    // No products matched an active filter (distinct from the true-empty state).
    filterRejectedTitle: "These filters are not valid",
    filterRejectedBody:
      "The catalogue could not answer this combination of filters. Clear them and narrow the list again.",
    noMatchTitle: "No matching products",
    noMatchBody: "No products match these filters.",
    clearFilters: "Clear filters",
    // The filter row lives in the products Card header (§3.3): each control is
    // named by its aria-label and states its own "any" option, so a filter set
    // reads as a sentence instead of four identical "Any"s.
    filters: {
      kind: "Kind",
      status: "Status",
      schema: "Attribute schema",
      makeOrBuy: "Make or buy",
      attribute: "Attribute",
      anyAttribute: "Any attribute",
      attributeOp: "Comparison",
      attributeValue: "Value",
      op: {
        eq: "is",
        gte: "at least",
        lte: "at most",
      },
      anyKind: "Any kind",
      anyStatus: "Any status",
      anySchema: "Any schema",
      anyMakeOrBuy: "Make or buy",
      clear: "Clear filters",
      more: "Filters",
      moreCount: "Filters ({{count}})",
      title: "Filters",
      done: "Done",
      remove: "Remove {{filter}}",
      chip: "{{filter}}: {{value}}",
    },
    col: {
      select: "Select",
      sku: "SKU",
      name: "Name",
      kind: "Kind",
      baseUom: "Base unit",
      status: "Status",
      attributes: "Attributes",
      cost: "Standard cost",
    },
    makeOrBuy: {
      make: "Make",
      buy: "Buy",
      both: "Both",
    },
    bulk: {
      regionLabel: "Bulk actions",
      selectedCount: "{{count}} selected",
      selectAll: "Select all",
      clear: "Clear selection",
      activate: "Activate",
      discontinue: "Discontinue",
      running: "Working…",
      done: "{{count}} products updated.",
      partial: "{{ok}} updated, {{failed}} refused. Open the refused ones to see why.",
      failedFallback: "The bulk update did not go through. Nothing was changed.",
    },
    page: {
      prev: "Previous",
      next: "Next",
      showing: "Showing {{count}}",
    },
    chipMore: "+{{count}} more",
    // Inline create flow: template picker first, then the draft form.
    create: {
      pickTitle: "Start a new product",
      pickBody: "Choose a template to prefill attributes, or start from scratch.",
      blank: "Blank product",
      blankHint: "No template — pick an attribute schema yourself.",
      noTemplates: "No product templates in this workspace yet.",
      templatesError: "Could not load templates.",
      back: "Back to templates",
      formTitle: "New product",
      fromTemplate: "From template: {{name}}",
      sku: "SKU",
      name: "Name",
      kind: "Kind",
      baseUom: "Base unit of measure",
      baseUomPlaceholder: "Select a unit…",
      schema: "Attribute schema",
      schemaPlaceholder: "None",
      schemaLoading: "Loading schemas…",
      attributesTitle: "Attributes",
      submit: "Create product",
      submitting: "Creating…",
      created: "{{sku}} created. It stays a draft until you activate it.",
      cancel: "Cancel",
      error: "Could not create the product. Check the fields and try again.",
    },
    // Discontinue confirm (status-gated action; force when in an active BOM).
    discontinue: {
      title: "Discontinue product",
      body: "Discontinued products are hidden from new quotes and plans. They stay on existing orders and history.",
      // Shown ONLY after the server has REFUSED with PRODUCT_IN_ACTIVE_BOM and
      // told us how many BOMs. The dialog used to assert this unconditionally,
      // about every product, including ones created seconds earlier and used by
      // nothing — a claim it had never checked.
      inUseFound:
        "This product is a component on {{count}} active BOM(s). Discontinuing it anyway leaves them referencing a discontinued part.",
      force: "Discontinue anyway",
      confirm: "Discontinue",
      cancel: "Cancel",
    },
  },

  // ---- S2 Product detail (/catalogue/products/:id) -------------------------
  productDetail: {
    loading: "Loading product…",
    errorTitle: "Could not load product",
    notFound: "Product not found.",
    header: {
      qrPrint: "Print QR card",
      qrPrinting: "Preparing…",
      working: "Working…",
      qrDraft: "QR codes are issued when a product is activated.",
      qrUnavailable: "QR card export isn't available.",
      activate: "Activate",
      discontinue: "Discontinue",
      reactivate: "Reactivate",
      // §3.8: one sentence per lifecycle change, beside the flipped chip.
      done: {
        activate: "Activated. This product can now be planned, sold and produced.",
        discontinue: "Discontinued. It stays on existing orders and history.",
        reactivate: "Reactivated. It is available for new work again.",
      },
    },
    tabsLabel: "Product detail sections",
    tabs: {
      details: "Details",
      attributes: "Attributes",
      uom: "UoM & conversions",
      boms: "BOMs",
      routings: "Routings",
      whereUsed: "Where-used",
      cost: "Cost",
      history: "History",
    },
    // S2 master data — the fields PATCH /products/:id accepts. Without this the
    // product's own name and SKU were the only facts in the module a user could
    // not correct after typing them.
    details: {
      title: "Product details",
      sku: "SKU",
      skuLocked: "The SKU is fixed once a product is activated.",
      name: "Name",
      description: "Description",
      kind: "Kind",
      baseUom: "Base unit",
      fixedHint: "Set when the product was created and not editable here.",
      makeOrBuy: "Make or buy",
      tracking: "Tracking",
      assemblyScrapPct: "Assembly scrap %",
      purchaseLeadDays: "Purchase lead time (days)",
      manufactureLeadDays: "Manufacturing lead time (days)",
      priceUnitQty: "Price per quantity",
      priceUnitQtyHint: "Costs and prices are quoted per this many base units (e.g. 1000 m).",
      defaultFacility: "Default facility",
      noFacility: "No default facility",
      save: "Save details",
      saving: "Saving…",
      saved: "Details saved.",
      error: "Could not save the details.",
      readOnly: "You do not have permission to edit this product.",
    },
    attributes: {
      title: "Attributes",
      none: "This product has no attributes.",
      noSchema: "No attribute schema is assigned to this product.",
      save: "Save attributes",
      saving: "Saving…",
      saved: "Attributes saved.",
      error: "Could not save the attributes.",
    },
    uom: {
      title: "Units & conversions",
      base: "Base unit",
      conversionsTitle: "Product-specific conversions",
      from: "From",
      to: "To",
      factor: "Factor",
      none: "No product-specific conversions.",
      loading: "Loading conversions…",
      error: "Could not load conversions.",
    },
    boms: {
      title: "Bills of materials",
      none: "No BOM versions yet.",
      loading: "Loading BOMs…",
      error: "Could not load BOMs.",
      open: "Open",
      phantom: "Phantom",
      facilityWide: "Group-wide",
      version: "Version",
    },
    routings: {
      title: "Routings",
      none: "No routing versions yet.",
      loading: "Loading routings…",
      error: "Could not load routings.",
      open: "Open",
      facility: "Facility",
      version: "Version",
    },
    whereUsed: {
      title: "Where used",
      asComponent: "Used as a component",
      asOutput: "Produced as an output",
      none: "This product is not referenced by any BOM.",
      loading: "Loading references…",
      error: "Could not load references.",
      version: "v{{version}}",
    },
    cost: {
      title: "Standard cost",
      standard: "Released standard cost",
      notCosted: "Not costed yet.",
      run: "From cost run",
      breakdown: "Cost breakdown",
      loading: "Loading cost…",
      error: "Could not load cost.",
    },
    history: {
      title: "Change history",
      none: "No engineering changes recorded.",
      loading: "Loading history…",
      error: "Could not load history.",
      effectiveFrom: "Effective {{date}}",
      entityBom: "BOM",
      entityRouting: "Routing",
      recordedBy: "Recorded {{date}} · {{actor}}",
    },
  },

  // S3 BOM editor (§4 S3, §2.6–2.9, §3.3). Nested per the file header so this
  // Stage-2 region stays conflict-free from the sibling screen builders.
  bomEditor: {
    title: "Bill of materials",
    version: "Version {{version}}",
    outputQty: "Output quantity",
    outputUom: "Output unit",
    nameLabel: "Label",
    namePlaceholder: "e.g. post-armour-change",
    phantom: "Phantom",
    headerTitle: "Batch",
    saveHeader: "Save header",
    // One saved-notice for the whole editor (§3.8) — header, components and
    // outputs all report into the same banner rather than three fragments.
    savedHeader: "Batch saved.",
    savedLines: "{{count}} components saved.",
    savedOutputs: "{{count}} outputs saved.",
    // Load / empty / error states (§4).
    loadingLabel: "Loading the bill of materials…",
    errorTitle: "Could not load the bill of materials",
    emptyLinesTitle: "No components yet",
    emptyLinesBody: "Add the first component to build this bill of materials.",
    // Draft / read-only banners.
    draftBanner: "Draft — edit components and outputs, then activate.",
    readOnlyBanner: "This version is {{status}} and cannot be edited.",
    // A future-dated activation (spec 01 §9.16, D-261): amber, because a
    // version that is active-but-not-in-force needs a human to know it.
    scheduledBanner: "Scheduled — this version takes effect on {{date}} and is not in force yet.",
    createNewVersion: "Create new version",
    creatingVersion: "Creating…",
    // §3.8 — creating a version WRITES a row and changes the URL; it confirmed
    // nothing, so two clicks silently produced v3 and v4.
    versionCreated: "Draft v{{version}} created as a copy of v{{from}}.",
    // Left component tree.
    tree: {
      heading: "Components",
      addLine: "Add component",
      expand: "Expand",
      collapse: "Collapse",
      netOfScrap: "net of scrap",
      perOutput: "per output",
      structureUnavailable: "Nested structure is unavailable for this draft.",
      unknownComponent: "Unknown component",
    },
    // Right line inspector.
    inspector: {
      heading: "Line inspector",
      selectPrompt: "Select a component on the left to edit it.",
      component: "Component",
      componentPlaceholder: "Select a component…",
      qty: "Quantity",
      uom: "Unit",
      scrapPct: "Scrap %",
      consumeAtStep: "Consume at step",
      atWoStart: "At work-order start",
      optional: "Optional component",
      notes: "Notes",
      substitutes: "Substitutes",
      addSubstitute: "Add substitute",
      substitutePlaceholder: "Select a substitute…",
      priority: "Priority",
      qtyFactor: "Qty factor",
      removeSubstitute: "Remove",
      removeLine: "Remove component",
      save: "Save components",
      // The BUSY label. Load-bearing, not decorative — see Button.busyLabel.
      saving: "Saving…",
      unsaved: "Unsaved changes",
    },
    // Outputs panel (co-products + by-products).
    outputs: {
      heading: "Outputs",
      coProducts: "Co-products",
      byProducts: "By-products",
      addCoProduct: "Add co-product",
      addByProduct: "Add by-product",
      primaryShare: "Primary",
      coProductShare: "Co-products",
      remainder: "{{pct}}% to primary",
      shareBarLabel: "Batch value: {{primary}}% to the primary output, {{total}}% to co-products.",
      overAllocated: "Co-products allocate {{total}}% — must be under 100%.",
      valid: "Co-products allocate {{total}}%.",
      product: "Product",
      productPlaceholder: "Select a product…",
      qty: "Quantity",
      uom: "Unit",
      apportionmentPct: "Apportionment %",
      nrv: "NRV (per price unit)",
      nrvPriceUnitQty: "Price unit qty",
      currency: "Currency",
      nrvRedacted: "NRV hidden — you do not have cost access.",
      addByProductNoCost: "Adding by-products needs cost access.",
      removeOutput: "Remove",
      save: "Save outputs",
      saving: "Saving…",
      saveBlocked: "Co-products must allocate under 100% before saving.",
      none: "No outputs beyond the primary product.",
    },
    // Activate dialog.
    activate: {
      button: "Activate",
      dialogTitle: "Activate this version",
      effectiveFrom: "Effective from",
      note: "Activation note",
      notePlaceholder: "Why is this version changing? (required)",
      noteRequired: "An activation note is required.",
      pastDate: "The effective date cannot be in the past.",
      overlapPreview: "Version {{version}} will be superseded effective {{date}}.",
      conflictPreview: "Version {{version}} already covers that date — choose a later date.",
      noOverlap: "No active version to supersede.",
      saveFirst: "Save your changes before activating.",
      fixApportionment: "Fix co-product apportionment (must total under 100%) before activating.",
      confirm: "Activate",
      activating: "Activating…",
      cancel: "Cancel",
      success: "Activated. This version is now effective.",
      // The post-activation line when the date was in the future — `success`
      // above used to claim "now effective" for a 2030 date (D-261).
      scheduled: "Activation scheduled for {{date}}. This version is not in force yet.",
    },
    // Version switcher + diff.
    diff: {
      compareAgainst: "Compare against",
      selectVersion: "Select a version…",
      loading: "Loading comparison…",
    },
    // Async component/substitute typeahead (searches the full product universe).
    picker: {
      change: "Change",
      searching: "Searching…",
      noMatches: "No matching products.",
      failed: "Could not search products. Try again.",
    },
  },

  // S4 Routing editor. Ordered step list, per-step drawer, lead-time preview,
  // activate dialog, BOM consume-at-step warning, version diff (§4 S4, §2.11,
  // §3.5). No cost surface on this screen (§4) — nothing here is cost-gated.
  routing: {
    title: "Routing",
    loading: "Loading routing…",
    errorTitle: "Could not load the routing",
    badRoute: "This routing address is incomplete. Open a product and pick a routing version.",
    unsaved: "Unsaved changes",
    allSaved: "All changes saved",
    addStep: "Add step",
    createNewVersion: "Create new version",
    versionCreated: "Draft v{{version}} created as a copy of v{{from}}.",
    readOnlyBanner:
      "This routing is {{status}} and cannot be edited. Create a new version to make changes.",
    // A future-dated activation (spec 01 §9.16, D-261).
    scheduledBanner:
      "Scheduled — this routing takes effect on {{date}} and is not in force yet. Its steps cannot be edited.",
    consumeWarning:
      "A bill of materials consumes components at step {{steps}}, which no longer exists in this routing.",
    versionsTitle: "Versions",
    stepsTitle: "Steps",
    compareWith: "Compare with",
    emptyTitle: "No steps yet",
    emptyBody:
      "This routing has no operations. Add the first step to define how the product is made.",
    emptyCta: "Add first step",
    unnamedStep: "Untitled step",
    leadTime: {
      label: "Lead time / 1000 m",
      value: "{{minutes}} min ({{hours}} h)",
      sub: "across {{count}} steps",
    },
    save: {
      save: "Save changes",
      saving: "Saving…",
      error: "Could not save the steps. Check the fields and try again.",
      fixSteps: "Fix the errors on step {{steps}} before saving.",
    },
    columns: {
      step: "Step",
      operation: "Operation",
      workCenter: "Work center",
      times: "Times",
      actions: "Actions",
    },
    runSummary: {
      line: "Setup {{setup}} min · {{run}}",
      perUnit: "{{minutes}} min/unit",
      perBatch: "{{minutes}} min/batch",
    },
    actions: {
      moveUp: "Move step {{n}} up",
      moveDown: "Move step {{n}} down",
      view: "View",
      edit: "Edit",
      remove: "Remove step {{n}}",
    },
    flags: {
      outsourced: "Outsourced",
      measured: "Measured capture",
      serial: "Serial capture",
      manualIssue: "Manual issue",
      overlap: "Overlap",
      qc: "QC gate",
      alternates: "{{n}} alternate WC",
      instructions: "{{n}} instruction blocks",
    },
    fields: {
      name: "Operation name",
      namePlaceholder: "e.g. PVC coat",
      workCenter: "Work center",
      setupMinutes: "Setup minutes",
      runBasis: "Run basis",
      runMinutesUnit: "Run minutes / unit",
      runMinutesBatch: "Run minutes / batch",
      batchQty: "Batch quantity",
      setupKey: "Setup key",
      setupKeyPlaceholder: "e.g. die:70mm2",
      setupKeyHint: "Same key = zero changeover on the schedule.",
      operationScrapPct: "Operation scrap %",
      alternateWorkCenters: "Alternate work centers",
      qcControlPoint: "QC control point",
      qcControlPointPlaceholder: "Quality control point id",
      qcControlPointHint: "Attaching a QC control point inserts a quality gate after this step.",
      captureGroup: "Floor capture",
      requiresMeasuredCapture: "Requires measured capture",
      requiresSerialCapture: "Requires serial capture",
      backflush: "Backflush components at this step",
      canOverlap: "Next step may start on partial transfer",
      outsourced: "Outsourced step",
      minCrew: "Minimum crew",
      outsourceCode: "Outsource contract code",
      outsourceCodePlaceholder: "Supplier operation code",
      instructions: "Work instructions",
    },
    workCenters: {
      loading: "Loading work centers…",
      error: "Could not load work centers.",
      empty: "No active work centers in this facility.",
      placeholder: "Select a work center…",
      loadHint: "Only this facility's work centers are shown.",
      noAlternates: "No other work centers available.",
    },
    runBasis: {
      per_unit: "Per unit",
      per_batch: "Per batch",
    },
    errors: {
      nameRequired: "Enter an operation name.",
      workCenterRequired: "Choose a work center.",
      runMinutesInvalid: "Enter run minutes of zero or more.",
      batchQtyRequired: "Enter a batch quantity for a per-batch step.",
      outsourceCodeRequired: "Enter the outsource contract code.",
    },
    instructions: {
      empty: "No instruction blocks yet.",
      textLabel: "Instruction text, block {{n}}",
      imageKey: "Image key",
      imageKeyPlaceholder: "S3 object key",
      imageAlt: "Alt text",
      uploadUnavailable: "Image upload isn't available. Enter the key of an existing file.",
      moveUp: "Move block up",
      moveDown: "Move block down",
      removeBlock: "Remove block",
      addText: "Add text",
      addImage: "Add image",
    },
    drawer: {
      titleNew: "New step",
      titleEdit: "Edit step {{n}}",
      titleView: "Step {{n}}",
      readOnly:
        "This routing is not a draft, so its steps cannot be changed. Create a new version to edit it.",
      close: "Close",
      delete: "Delete step",
      cancel: "Cancel",
      apply: "Apply",
    },
    activate: {
      open: "Activate",
      title: "Activate routing",
      unsavedWarning: "Save your changes before activating — activation uses the saved draft.",
      noStepsWarning: "A routing needs at least one step before it can be activated.",
      consumeMismatchWarning:
        "A bill of materials consumes at step {{steps}}, which is not in this routing. Activation is still allowed (versions are independent).",
      supersedePreview: "Version {{version}} will be superseded on the effective date.",
      effectiveFrom: "Effective from",
      effectiveFromHint: "Today or a future date. Back-dating is rejected.",
      note: "Activation note",
      notePlaceholder: "Reason for this change (the light ECO record)",
      cancel: "Cancel",
      confirm: "Activate",
      activating: "Activating…",
    },
  },

  // S5 Units of measure (/catalogue/uoms). Screen group: uom-templates. These
  // keys were a runtime bundle in the group's branch; promoted here at merge so
  // the static i18n:parity gate (D-014) covers them like every other screen.
  uom: {
    title: "Units of measure",
    subtitle: "Units and conversions used across products, BOMs and routings.",
    unitsTitle: "Units",
    conversionsTitle: "Conversions",
    colCode: "Code",
    colName: "Name",
    colDimension: "Dimension",
    colBase: "Base",
    colPrecision: "Precision",
    colSource: "Source",
    colStatus: "Status",
    colActions: "Actions",
    colFrom: "From",
    colTo: "To",
    colFactor: "Factor",
    baseBadge: "Base",
    addUnit: "Add unit",
    addConversion: "Add conversion",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    archive: "Archive",
    remove: "Remove",
    showArchived: "Show archived",
    // §3.8: each unit/conversion mutation states its outcome once, in words.
    unitCreated: "Unit {{code}} added.",
    unitSaved: "Unit {{code}} saved.",
    unitArchived: "Unit {{code}} archived.",
    conversionCreated: "Conversion added.",
    conversionRemoved: "Conversion removed.",
    archivedHiddenBody: "Every unit here is archived. Show archived units to see them.",
    codeLabel: "Code",
    nameLabel: "Name",
    dimensionLabel: "Dimension",
    baseLabel: "Base unit for its dimension",
    baseAuto: "First unit in this dimension — it becomes the base.",
    precisionLabel: "Decimal precision",
    factorLabel: "Factor",
    fromLabel: "From unit",
    toLabel: "To unit",
    selectPlaceholder: "Select…",
    factorHint: "qty_to = qty_from × factor. The reverse conversion is derived automatically.",
    sameDimensionHint: "Both units must share a dimension.",
    reciprocal: "1 {{from}} = {{factor}} {{to}}",
    dimension: {
      length: "Length",
      mass: "Mass",
      count: "Count",
      area: "Area",
      volume: "Volume",
      time: "Time",
      container: "Container",
    },
    source: {
      vertical_pack: "Pack",
      custom: "Custom",
    },
    unitsEmptyTitle: "No units yet",
    unitsEmptyBody: "Add a base unit for each dimension you measure.",
    conversionsEmptyTitle: "No conversions yet",
    conversionsEmptyBody: "Add a conversion between two units of the same dimension.",
    createFailed: "Could not save the unit. Check the fields and try again.",
    updateFailed: "Could not update the unit. Try again.",
    archiveFailed: "Could not archive the unit. Try again.",
    conversionCreateFailed: "Could not save the conversion. Check the fields and try again.",
    conversionDeleteFailed: "Could not remove the conversion. Try again.",
    inUse: "In use by {{products}} product(s) and {{bomLines}} BOM line(s) — cannot archive.",
  },

  // S8 Templates (/catalogue/templates). Screen group: uom-templates. Promoted
  // from the group's runtime bundle into this gated file at merge (see uom note).
  templates: {
    title: "Templates",
    subtitle: "Starters for new products, BOMs and routings.",
    newTemplate: "New template",
    use: "Use",
    archive: "Archive",
    viewPayload: "View payload",
    hidePayload: "Hide payload",
    showArchived: "Show archived",
    created: "Template {{name}} saved.",
    archived: "Template {{name}} archived.",
    group: {
      product: "Product templates",
      bom: "BOM templates",
      routing: "Routing templates",
    },
    kind: {
      product: "Product",
      bom: "BOM",
      routing: "Routing",
    },
    source: {
      vertical_pack: "Pack",
      custom: "Custom",
    },
    preview: {
      baseUom: "Base unit",
      productKind: "Kind",
      tracking: "Tracking",
      makeOrBuy: "Make / buy",
      attributes: "{{count}} attribute(s)",
      steps: "{{count}} step(s)",
      lines: "{{count}} component(s)",
      outputUom: "Output unit",
      phantom: "Phantom",
      none: "No preview available.",
    },
    useProductNote: "Opening the product create form with this template.",
    useStructureNote: "Select a product, then create a {{kind}} from this template.",
    form: {
      title: "New custom template",
      nameLabel: "Name",
      codeLabel: "Code",
      kindLabel: "Kind",
      modeLabel: "Contents",
      modeEmpty: "Empty starter",
      modeSnapshot: "Snapshot from existing",
      sourceProductLabel: "Source product",
      sourceEntityLabel: "Source id",
      sourceEntityHint: "Paste the BOM or routing id to snapshot its contents.",
      create: "Save template",
      cancel: "Cancel",
      createFailed: "Could not save the template. Check the fields and try again.",
      loadingProducts: "Loading products…",
    },
    archiveFailed: "Could not archive the template. Try again.",
    emptyTitle: "No templates yet",
    emptyBody: "Save a product, BOM or routing as a reusable template.",
    archivedOnlyTitle: "Every template here is archived",
    archivedOnlyBody: "Show archived templates to see them, or save a new one.",
  },
} as const;

// Shape with values widened to `string` (src/i18n-shape.ts); keys stay parity-
// checked at compile time.
export type CatalogueDict = Translated<typeof catalogueEn>;

// ru is TRANSLATED (catalogue.ru.ts); the other locales import their own files
// from src/locales/<locale>.ts. The claims live in src/i18n-completeness.ts and
// are enforced by the CI gate.
export const catalogueRu: CatalogueDict = catalogueRuDict;
